import { ReactElement } from 'react';

import { PrescriptionP1HistoryPrescription } from '@dreryk/egabinet-prescription-frontend-api';

import { JustifyContent, Padding, RowDetailsCell, Text } from '@dreryk/react-components';

import {
   ContinuouslyUsedMedicineButton,
   MedicineFullNameReadOnlyCell,
   MedicineSafetyIndicators,
} from '~libs.desktop.shared.medicine';
import { formatMedicinePackage } from '~libs.shared.medicine';

import { P1PrescriptionIssuedFromCell } from './P1PrescriptionIssuedFromCell';
import { P1PrescriptionHistoryColumnsWidth } from './P1PrescriptionsHistoryTable.utils';
import { StyledP1PrescriptionRowDetails } from './P1PrescriptionRowDetails.styled';

type P1PrescriptionRowDetailsProps = {
   readonly prescription: PrescriptionP1HistoryPrescription;
   readonly columnsWidth: P1PrescriptionHistoryColumnsWidth;
   readonly patientId: number;
   readonly onMedicineProcessed: VoidFunction;
   readonly safetyIndicatorsVisible: boolean;
   readonly testId: string;
};

const renderMedicineName = (prescription: PrescriptionP1HistoryPrescription): ReactElement => {
   if (prescription.medicine) {
      return (
         <MedicineFullNameReadOnlyCell
            medicine={prescription.medicine}
            reimbursementCode={prescription.reimbursementCode}
         />
      );
   }
   return <Text testId={'medicineName'}>{prescription.medicineName ?? null}</Text>;
};

const renderPackage = (prescription: PrescriptionP1HistoryPrescription): string | null => {
   if (prescription.medicine) {
      return formatMedicinePackage(prescription.medicine);
   }
   return prescription.medicinePackageContent ?? null;
};

export const P1PrescriptionRowDetails = (props: P1PrescriptionRowDetailsProps): JSX.Element => (
   <StyledP1PrescriptionRowDetails testId={props.testId}>
      <RowDetailsCell testId={'medicineName'} width={props.columnsWidth.medicineName}>
         {renderMedicineName(props.prescription)}
      </RowDetailsCell>
      {props.safetyIndicatorsVisible ? (
         <RowDetailsCell testId={'safetyIndicators'} width={props.columnsWidth.safetyIndicators}>
            {props.prescription.medicine ? (
               <MedicineSafetyIndicators medicineId={props.prescription.medicine.id} />
            ) : null}
         </RowDetailsCell>
      ) : null}
      <RowDetailsCell testId={'package'} width={props.columnsWidth.package}>
         <Text testId={'package'}>{renderPackage(props.prescription)}</Text>
      </RowDetailsCell>
      <RowDetailsCell testId={'quantity'} width={props.columnsWidth.quantity}>
         <Text testId={'quantity'}>{props.prescription.quantity ?? null}</Text>
      </RowDetailsCell>
      <RowDetailsCell
         testId={'issuedFrom'}
         width={props.columnsWidth.issuedFrom}
         justifyContent={JustifyContent.CENTER}
         padding={Padding.NONE}
      >
         <P1PrescriptionIssuedFromCell prescription={props.prescription} />
      </RowDetailsCell>
      <RowDetailsCell testId={'options'} width={props.columnsWidth.expander} justifyContent={JustifyContent.END}>
         {props.prescription.medicine ? (
            <ContinuouslyUsedMedicineButton
               patientId={props.patientId}
               medicineId={props.prescription.medicine.id}
               medicineValid={props.prescription.medicine.valid}
               continuouslyUsedMedicineId={props.prescription.patientContinuouslyUsedMedicineId}
               medicineName={props.prescription.medicine.fullName}
               onMedicineProcessed={props.onMedicineProcessed}
            />
         ) : null}
      </RowDetailsCell>
   </StyledP1PrescriptionRowDetails>
);
